/**
 * Core class for Facebook integration. It asynchronously loads
 * the Facebook SDK. Requires an empty &lt;div&gt; with an id of "fb-root"
 * to already be present in the document when instantiated.
 * @constructor
 * @extends {Foundation.Promise}
 * @param {string=} appId
 * @param {Object=} options
 */
Foundation.Facebook.Application = function (appId, options) {
    Foundation.Promise.call(this);

    /**
     * The application ID assigned by Facebook to this application.
     * @type {string}
     * @public
     */
    this.appId = appId ? appId : Foundation.Facebook.Application.sharedAppId();

    var ApiVersion = "v2.9"

    if (typeof window.__FBAPIv__ === 'string') {
        ApiVersion = window.__FBAPIv__
    }

    /**
     * Options passed to <code>FB.init()</code>.
     * @type {Object}
     * @public
     */
    this.options = Foundation.extend({
        "appId": this.appId,
        "status": true,
        "cookie": true,
        "xfbml": true,
        "version": ApiVersion
    }, options);

    /**
     * Callback for sessionChange FB.Event.
     * @private
     */
    this._boundSessionChangeMethod = this.sessionChange.bind(this);

    /**
     * Whether or not the application is listening for
     * auth response changes.
     * @private
     * @type {boolean}
     */
    this._listeningForAuth = false;

    // Figure out what URL to use for the Facebook JS SDK library.
    var fbJS = `https://connect.facebook.net/en_US/sdk${  
        Foundation.Dev.isDebugJSActive() ? "/debug.js" : ".js"}`;

    // Load the Facebook JS SDK library.
    var script = new Foundation.Script(fbJS, "fbAsyncInit")
    this.marry(script);
    this.then(function () {
        FB.init(this.options);
    }.bind(this));

    // Set the id attribute of the script tag to attempt to prevent
    // copy-and-pasted Facebook code from doing FB.init() all over again.
    script.setElementId("facebook-jssdk");

    // Save this app instance so other code can easily interact with Facebook.
    Foundation.Facebook.Application[`_shared_${this.appId}`] = this;
};
Foundation.inherit(Foundation.Facebook.Application, Foundation.Promise);

/**
 * Static function to grab and return the Facebook app ID from the document.
 * @return {string}
 */
Foundation.Facebook.Application.sharedAppId = function () {
    return /** @type {string} */ (jQuery('meta[property="fb:app_id"]').attr("content"));
};

/**
 * Static function to retrieve shared Facebook.Application instance. If there is
 * no shared Facebook.Application instance, then return null.
 * @param {string=} appId
 */
Foundation.Facebook.Application.sharedInstance = function (appId) {
    if (!appId) appId = Foundation.Facebook.Application.sharedAppId();
    if (Foundation.Facebook.Application[`_shared_${appId}`]) {
        return Foundation.Facebook.Application[`_shared_${appId}`];
    }
    else {
        return null;
    }
};

/**
 * Register the Facebook session manager delegate with the shared session manager.
 */
Foundation.Facebook.Application.prototype.receiveAuthNotifications = function () {
    if (Foundation.Facebook.Application._sessionManagerDelegateRegistered) return;
    var delegate = new Foundation.Facebook.SessionManagerDelegate();
    Foundation.Session.Manager.sharedInstance().registerDelegate(delegate);
    Foundation.Facebook.Application._sessionManagerDelegateRegistered = true;
};

/**
 * Start watching for Facebook session changes.
 */
Foundation.Facebook.Application.prototype.listenForAuthResponseChange = function () {
    var sessionManager = Foundation.Session.Manager.sharedInstance();
    this.then(function () {
        if (this._listeningForAuth) return;
        this._listeningForAuth = true;

        // When the login status changes we need to revalidate the login.
        FB.Event.subscribe("auth.statusChange", this._boundSessionChangeMethod);

        // Session may have changed while we weren't listening, so let's do
        // a check, but only if we're logged in.
        if (sessionManager.isAuthenticated()) {
            FB.getLoginStatus(this._boundSessionChangeMethod);
        }
    }.bind(this));
};

/**
 * Stop watching for Facebook session changes.
 */
Foundation.Facebook.Application.prototype.unlistenForAuthResponseChange = function () {
    this.then(function () {
        if (!this._listeningForAuth) return;
        FB.Event.unsubscribe("auth.statusChange", this._boundSessionChangeMethod);
        this._listeningForAuth = false;
    }.bind(this));
};

/**
 * Log the user into Facebook.
 * @param {string=} perms Comma separated list of extended permissions to ask for.
 * @return {Foundation.Promise}
 */
Foundation.Facebook.Application.prototype.logIn = function (perms) {
    var loginPromise = new Foundation.Promise();

    // window.console.log('Foundation.Facebook.Application.logIn', perms)

    FB.login(function (response) {
        // window.console.log("FB.login response", response)
        // FB.login returns response.authResponse for successful login
        if (response.authResponse) {
            loginPromise.fulfillWith(response);
        }
        else {
            loginPromise.failWith(response);
        }
    }.bind(this), { "scope": perms });

    return loginPromise;
};

/**
 * Log out the user from Facebook.
 * @return {Foundation.Promise}
 */
Foundation.Facebook.Application.prototype.logOut = function () {
    var logoutPromise = new Foundation.Promise();

    // window.console.log('Foundation.Facebook.Application.logOut')

    this.isAuthenticated().then(function () {
        // User is logged in to Facebook and our app, let's log them out.
        FB.logout(function (response) {
            // window.console.log("FB.logout response", response)
            logoutPromise.fulfillWith(response);
        });
    }, function () {
        logoutPromise.fulfill();
    })

    return logoutPromise;
};

/**
 * Handle a user logging in or logging out of Facebook.
 * @param {Object} fbResponse Facebook session change response.
 */
Foundation.Facebook.Application.prototype.sessionChange = function (fbResponse) {
    var sessionManager = Foundation.Session.Manager.sharedInstance();

    // var fbLoggedIntoSite = fbResponse.status === "connected" && fbResponse.authResponse !== null
    var fbLoggedOut = fbResponse.status === "unknown" && fbResponse.authResponse === null

    // window.console.log("Foundation.Facebook.Application.sessionChange called", "fbLoggedIntoSite", fbLoggedIntoSite, "fbLoggedOut", fbLoggedOut, fbResponse)

    // This was causing a double login attempt.
    // if (fbLoggedIntoSite) {
    //     if (sessionManager.isAuthenticated()) return;
    //     var authenticator = new Foundation.Facebook.Authenticator();
    //     authenticator.authenticateWithFoundation().then(function () {
    //         var member = new Foundation.Member(authenticator.memberOid());
    //         member.set("userName", authenticator.memberUserName());
    //         member.set("avatarUrl", authenticator.memberAvatarUrl());
    //         sessionManager.logIn(authenticator.sessionManagerDelegateID(), member);
    //     }.bind(this));
    // }
    // else if (fbLoggedOut) {
    //     // The user has logged out of Facebook, and the cookie has been cleared,
    //     // so attempt to log out.
    //     if (sessionManager.isAuthenticated()) sessionManager.logOut();
    // }

    if (fbLoggedOut) {
        // The user has logged out of Facebook, and the cookie has been cleared,
        // so attempt to log out.
        if (sessionManager.isAuthenticated()) sessionManager.logOut();
    }
};

/**
 * Set up this Foundation.Facebook.Application to receive notifications
 * when user likes something.
 */
Foundation.Facebook.Application.prototype.receiveLikeNotifications = function () {
    this.then((function () {
        FB.Event.subscribe("edge.create", this.edgeUpdate.bind(this,  1));
        FB.Event.subscribe("edge.remove", this.edgeUpdate.bind(this, -1));
    }).bind(this));
};

Foundation.Facebook.Application.prototype.edgeUpdate = function (increment, response) {
    new Foundation.API("POST", "Facebook/Like", {
        "increment": increment,
        "url": response
    });
};

/**
 * Return a promise that is fulfilled if the user is logged in and failed
 * if the user is logged out.
 * @return {Foundation.Promise}
 */
Foundation.Facebook.Application.prototype.isAuthenticated = function () {
    var result = new Foundation.Promise();

    // window.console.log("Foundation.Facebook.Application.isAuthenticated called")

    this.then(function () {
        FB.getLoginStatus(function (response) {
            // window.console.log("Foundation.Facebook.Application.isAuthenticated FB.getLoginStatus", response)
            if (response.status === "connected" && response.authResponse !== null) {
                result.fulfill();
            }
            else {
                result.fail();
            }
        });
    }.bind(this));

    return result;
};

/**
 * Uninstall the app from the Facebook user's Facebook account.
 * @return {Foundation.Promise}
 */
Foundation.Facebook.Application.prototype.uninstall = function () {
    return new Foundation.Facebook.API("DELETE", "/me/permissions");
};

/**
 * Parse any XFBML contained in an element.
 * @param {Element=} element
 */
Foundation.Facebook.Application.prototype.parseXFBML = function (element) {
    this.then(function () {
        FB.XFBML.parse(element);
    });
};

// ---------------------------------------------------------------------

window["Foundation"]["Facebook"]["Application"] = Foundation.Facebook.Application;
Foundation.Facebook.Application["sharedApplication"] = Foundation.Facebook.Application.sharedInstance;
Foundation.Facebook.Application["sharedInstance"] = Foundation.Facebook.Application.sharedInstance;
Foundation.Facebook.Application.prototype["receiveAuthNotifications"] = Foundation.Facebook.Application.prototype.receiveAuthNotifications;
Foundation.Facebook.Application.prototype["receiveLikeNotifications"] = Foundation.Facebook.Application.prototype.receiveLikeNotifications;

// ---------------------------------------------------------------------
